import { z } from "zod"

import { documentTypeKeys } from "../../primitives.js"
import { exportTemplates, reviewFlagTypes } from "../../taxonomy.js"
import {
  selectableWorkflowTriggerValues,
  WorkflowActionSchema,
  WorkflowConditionSchema,
  workflowArrivalSources,
  workflowComparisons,
  workflowConditionFields,
  workflowLimits,
  workflowPrerequisiteKeys,
  workflowReconciliationStatuses,
  workflowRunOutcomes,
  workflowStatuses,
  workflowValueKinds,
} from "../../workflows/index.js"
import { ApiJsonObjectSchema } from "./common.js"

/* Mirrors the schemas in apps/api/src/routes/workflows/workflow.schemas.ts.
   Change both together or the api-client silently drops the new fields: the
   client parses responses through these, so a field the API adds and this file
   does not know about never reaches the page. */

// --- the rule ----------------------------------------------------------------

export const ApiWorkflowStatusSchema = z.enum(workflowStatuses)

export type ApiWorkflowStatus = z.infer<typeof ApiWorkflowStatusSchema>

export const ApiCreateWorkflowBodySchema = z.object({
  name: z.string().trim().min(1).max(workflowLimits.nameMaxLength),
  triggerEventType: z.enum(selectableWorkflowTriggerValues),
  documentTypeKey: z.enum(documentTypeKeys).nullable(),
  conditions: z.array(WorkflowConditionSchema).max(workflowLimits.maxConditions),
  actions: z.array(WorkflowActionSchema).min(1).max(workflowLimits.maxActions),
  /**
   * Omitted means turned off. A rule that reaches outside LedgerBox is only
   * ever turned on by a deliberate second act, after the confirmation.
   */
  status: z.enum(["active", "paused"]).optional(),
})

export type ApiCreateWorkflowBody = z.infer<typeof ApiCreateWorkflowBodySchema>

export const ApiUpdateWorkflowBodySchema = z.object({
  name: z.string().trim().min(1).max(workflowLimits.nameMaxLength).optional(),
  triggerEventType: z.enum(selectableWorkflowTriggerValues).optional(),
  documentTypeKey: z.enum(documentTypeKeys).nullable().optional(),
  conditions: z
    .array(WorkflowConditionSchema)
    .max(workflowLimits.maxConditions)
    .optional(),
  actions: z
    .array(WorkflowActionSchema)
    .min(1)
    .max(workflowLimits.maxActions)
    .optional(),
  /** Also how pause and resume happen. */
  status: z.enum(["active", "paused"]).optional(),
})

export type ApiUpdateWorkflowBody = z.infer<typeof ApiUpdateWorkflowBodySchema>

export const ApiWorkflowRunOutcomeSchema = z.enum(workflowRunOutcomes)

export type ApiWorkflowRunOutcome = z.infer<typeof ApiWorkflowRunOutcomeSchema>

export const ApiWorkflowResponseSchema = z.object({
  workflowId: z.string().uuid(),
  name: z.string(),
  status: ApiWorkflowStatusSchema,
  triggerEventType: z.string(),
  documentTypeKey: z.string().nullable(),
  conditions: z.array(ApiJsonObjectSchema),
  actions: z.array(ApiJsonObjectSchema),
  /** The rule read back as one line, so the list and the page agree. */
  sentence: z.string(),
  consecutiveFailures: z.number().int().nonnegative(),
  systemPausedReason: z.string().nullable(),
  systemPausedAt: z.string().datetime().nullable(),
  lastRunAt: z.string().datetime().nullable(),
  /** Newest first. What the row of coloured marks is drawn from. */
  recentOutcomes: z.array(ApiWorkflowRunOutcomeSchema),
  createdByUserId: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

export type ApiWorkflowResponse = z.infer<typeof ApiWorkflowResponseSchema>

export const ApiWorkflowListResponseSchema = z.object({
  workflows: z.array(ApiWorkflowResponseSchema),
})

export type ApiWorkflowListResponse = z.infer<
  typeof ApiWorkflowListResponseSchema
>

export const ApiDeleteWorkflowResponseSchema = z.object({
  workflowId: z.string().uuid(),
  archivedAt: z.string().datetime(),
})

export type ApiDeleteWorkflowResponse = z.infer<
  typeof ApiDeleteWorkflowResponseSchema
>

// --- run history -------------------------------------------------------------

export const ApiWorkflowRunStepResponseSchema = z.object({
  workflowRunStepId: z.string().uuid(),
  stepIndex: z.number().int().nonnegative(),
  actionType: z.string(),
  state: z.string(),
  /** Pointers to the real thing it made, so history links rather than describes. */
  postingId: z.string().uuid().nullable(),
  webhookDeliveryId: z.string().uuid().nullable(),
  exportId: z.string().uuid().nullable(),
  errorClassification: z.string().nullable(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  durationMs: z.number().int().nullable(),
})

export type ApiWorkflowRunStepResponse = z.infer<
  typeof ApiWorkflowRunStepResponseSchema
>

export const ApiWorkflowRunResponseSchema = z.object({
  workflowRunId: z.string().uuid(),
  outcome: ApiWorkflowRunOutcomeSchema,
  skipReason: z.string().nullable(),
  /** A sentence for a skip, so "why did nothing happen" has an answer. */
  skipSentence: z.string().nullable(),
  documentId: z.string().uuid().nullable(),
  conditionResults: z.array(ApiJsonObjectSchema),
  startedAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
  durationMs: z.number().int().nullable(),
  steps: z.array(ApiWorkflowRunStepResponseSchema),
})

export type ApiWorkflowRunResponse = z.infer<
  typeof ApiWorkflowRunResponseSchema
>

export const ApiWorkflowRunListQuerySchema = z.object({
  outcome: ApiWorkflowRunOutcomeSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
})

export type ApiWorkflowRunListQuery = z.infer<
  typeof ApiWorkflowRunListQuerySchema
>

export const ApiWorkflowRunListResponseSchema = z.object({
  runs: z.array(ApiWorkflowRunResponseSchema),
  /** Printed on the panel itself rather than buried in a help page. */
  retention: z.object({
    actedDays: z.number().int(),
    skippedDays: z.number().int(),
  }),
})

export type ApiWorkflowRunListResponse = z.infer<
  typeof ApiWorkflowRunListResponseSchema
>

// --- the dry run -------------------------------------------------------------

export const ApiWorkflowTestResultSchema = z.object({
  documentId: z.string().uuid(),
  title: z.string().nullable(),
  matched: z.boolean(),
  /** Each check and the value it actually saw on this document. */
  conditionResults: z.array(ApiJsonObjectSchema),
})

export type ApiWorkflowTestResult = z.infer<typeof ApiWorkflowTestResultSchema>

export const ApiWorkflowTestResponseSchema = z.object({
  /** How many of the sample would have matched, and how many were considered. */
  matchedCount: z.number().int().nonnegative(),
  consideredCount: z.number().int().nonnegative(),
  results: z.array(ApiWorkflowTestResultSchema),
})

export type ApiWorkflowTestResponse = z.infer<
  typeof ApiWorkflowTestResponseSchema
>

/** The dry run may be asked about a rule that has not been saved yet. */
export const ApiWorkflowTestBodySchema = z
  .object({
    triggerEventType: z.enum(selectableWorkflowTriggerValues).optional(),
    documentTypeKey: z.enum(documentTypeKeys).nullable().optional(),
    conditions: z
      .array(WorkflowConditionSchema)
      .max(workflowLimits.maxConditions)
      .optional(),
  })
  .optional()

export type ApiWorkflowTestBody = z.infer<typeof ApiWorkflowTestBodySchema>

// --- the catalogue -----------------------------------------------------------

/* What this workspace can actually build right now. The editor renders from
   this rather than from a second copy of the registry, so a trigger, a field or
   an action the server would refuse can never be offered. */

export const ApiWorkflowTriggerSchema = z.object({
  value: z.string(),
  label: z.string(),
  description: z.string(),
})

export const ApiWorkflowComparisonSchema = z.object({
  value: z.enum(workflowComparisons),
  label: z.string(),
  valueKind: z.enum(workflowValueKinds),
  valueSuffix: z.string().optional(),
})

export const ApiWorkflowFieldSchema = z.object({
  value: z.enum(workflowConditionFields),
  label: z.string(),
  readsFrom: z.string(),
  documentTypeKeys: z.array(z.string()).nullable(),
  comparisons: z.array(ApiWorkflowComparisonSchema),
})

export const ApiWorkflowPrerequisiteSchema = z.object({
  key: z.enum(workflowPrerequisiteKeys),
  satisfied: z.boolean(),
  /** What is missing, and where to go and fix it. */
  label: z.string(),
  linkPath: z.string().nullable(),
})

export const ApiWorkflowActionSchema = z.object({
  value: z.string(),
  label: z.string(),
  description: z.string(),
  leavesLedgerBox: z.boolean(),
  documentTypeKeys: z.array(z.string()).nullable(),
  prerequisites: z.array(ApiWorkflowPrerequisiteSchema),
})

export const ApiWorkflowOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
})

export const ApiWorkflowCatalogResponseSchema = z.object({
  triggers: z.array(ApiWorkflowTriggerSchema),
  fields: z.array(ApiWorkflowFieldSchema),
  actions: z.array(ApiWorkflowActionSchema),
  /** The fixed lists behind a value control, already labelled. */
  options: z.object({
    flagType: z.array(ApiWorkflowOptionSchema),
    reconciliationStatus: z.array(ApiWorkflowOptionSchema),
    arrivalSource: z.array(ApiWorkflowOptionSchema),
    /** Workspace data, not a compile-time list: the workspace's categories. */
    category: z.array(ApiWorkflowOptionSchema),
  }),
  exportTemplates: z.array(
    z.object({
      value: z.enum(exportTemplates),
      label: z.string(),
      documentTypeKeys: z.array(z.string()),
      /** QuickBooks templates need a paid, active plan, as they do today. */
      requiresPaidPlan: z.boolean(),
    })
  ),
  webhookEndpoints: z.array(
    z.object({ webhookEndpointId: z.string().uuid(), name: z.string() })
  ),
  recipients: z.array(
    z.object({
      email: z.string(),
      confirmed: z.boolean(),
      bounced: z.boolean(),
    })
  ),
  documentTypes: z.array(ApiWorkflowOptionSchema),
  /** Named in the confirmation for a rule that posts. Null when not connected. */
  quickBooksCompanyName: z.string().nullable(),
  /** The accounts a posting rule writes between, named on the action row. */
  quickBooksDestination: z
    .object({
      bankAccountName: z.string().nullable(),
      offsetAccountName: z.string().nullable(),
    })
    .nullable(),
  /** The whole page sits behind one gate; the client renders locked from this. */
  entitled: z.boolean(),
})

export type ApiWorkflowCatalogResponse = z.infer<
  typeof ApiWorkflowCatalogResponseSchema
>

// --- email recipients --------------------------------------------------------

export const ApiCreateWorkflowRecipientBodySchema = z.object({
  email: z.string().email().max(320),
})

export type ApiCreateWorkflowRecipientBody = z.infer<
  typeof ApiCreateWorkflowRecipientBodySchema
>

export const ApiWorkflowRecipientResponseSchema = z.object({
  email: z.string(),
  confirmed: z.boolean(),
  bounced: z.boolean(),
  /** Whether this request sent a fresh confirmation, or it was already done. */
  confirmationSent: z.boolean(),
})

export type ApiWorkflowRecipientResponse = z.infer<
  typeof ApiWorkflowRecipientResponseSchema
>

export const workflowValueOptionSources = {
  flagType: reviewFlagTypes,
  reconciliationStatus: workflowReconciliationStatuses,
  arrivalSource: workflowArrivalSources,
} as const
